import React, { useContext, useEffect, useState, useRef } from 'react';
import { Abc3Context } from '../../context/Abc3Context';
import { CognitoAuthContext } from '../../context/CognitoAuthContext';
import { Container, Button, Modal } from 'react-bootstrap';
import { v4 as uuidv4 } from "uuid";
import { fetchAuthSession } from 'aws-amplify/auth';
import TradesGrid from './TradesGrid';
import TradeEntry from './TradeEntry';
import ConfirmTrade from './ConfirmTrade';
import { useTrades } from '../cache/TradesProvider';

const TradeBlotter = ({user}) => {
  const { account } = useContext(Abc3Context);
  const { userGroups } = useContext(CognitoAuthContext);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmedTrade, setShowConfirmedTrade] = useState(false);
  const [trade, setTrade] = useState({});
  const [viewOnly, setViewOnly] = useState(false);
  const [partyOrganization, setPartyOrganization] = useState('');

  const trades = useTrades();
  const tradesApiUrl = process.env.REACT_APP_TRADES_API;

  useEffect(() => {
    console.log('user => ', user);
  }, [user]);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!tradesApiUrl || !user?.party) {
        setPartyOrganization('');
        return;
      }

      try {
        const response = await fetch(`${tradesApiUrl}/parties`);
        if (!response.ok) {
          setPartyOrganization('');
          return;
        }
        const data = await response.json();
        const party = data.items?.find((p) => p.partyId === user.party);
        setPartyOrganization(party?.organization || party?.name || '');
      } catch (error) {
        console.error('Error fetching party organization:', error);
        setPartyOrganization('');
      }
    };

    fetchOrganization();
  }, [user?.party, tradesApiUrl]);

  const getRole = () => {
    if (userGroups?.includes('admin')) return 'admin';
    if (userGroups?.includes('manager')) return 'manager';
    if (userGroups?.includes('backoffice')) return 'backoffice';
    if (userGroups?.includes('trader') || userGroups?.includes('user')) return 'trader';
    return 'trader';
  };

  const canCreateTrade = () => {
    const role = getRole();
    return role === 'trader' || role === 'manager';
  };

  const canModifyTrade = (tradeData) => {
    const role = getRole();
    if (role === 'admin' || role === 'backoffice') return false;
    if (role === 'trader') {
      return tradeData.entity === user.party || tradeData.cpty === user.party;
    }
    if (role === 'manager') {
      return true;
    }
    return false;
  };

  const cancelTrade = async () => {
    if (!canModifyTrade(trade)) return;
    const t = { ...trade };
    t.status = 'cancelled';
    t.timestamp = Date.now();

    setViewOnly(false);
    setShowModal(false);

    await saveTrade(t);
  };

  const confirmTrade = async () => {
    if (!canModifyTrade(trade)) return;
    const t = { ...trade };
    t.status = 'confirmed';
    t.timestamp = Date.now();

    setViewOnly(false);
    setShowModal(false);

    await saveTrade(t);
  };

  const newTrade = () => {
    if (!canCreateTrade()) return;
    setShowModal(true);
    setTrade({});
  };

  const closeModal = () => {
    setViewOnly(false);
    setShowModal(false);
  };

  const openConfirmModal = () => {
    setShowModal(false);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setShowModal(true);
  };

  const closeConfirmedTrade = () => {
    setShowConfirmedTrade(false);
  };

  const sendTrade = () => {
    if (!canCreateTrade()) return;
    closeConfirmModal();
    closeModal();

    const updatedTrade = { ...trade };
    updatedTrade.status = 'sent';
    updatedTrade.timestamp = Date.now();
    if (user?.party) {
      if (updatedTrade.cpty === user.party && updatedTrade.entity !== user.party) {
        const originalEntity = updatedTrade.entity;
        updatedTrade.entity = user.party;
        updatedTrade.cpty = originalEntity;
      } else {
        updatedTrade.entity = user.party;
      }
    }
    const traderName = user?.name || '';
    const traderEmail = user?.email || account?.email || user?.username || account?.username || '';
    updatedTrade.trader = traderName || traderEmail;
    updatedTrade.traderName = traderName;
    updatedTrade.traderEmail = traderEmail;
    if (!updatedTrade.id && !updatedTrade.tradeId) {
      updatedTrade.id = uuidv4();
    }

    saveTrade(updatedTrade);
  };

  const saveTrade = async (tradeData) => {
    if (!tradesApiUrl) {
      alert('Trades API is not configured');
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!canCreateTrade() && !canModifyTrade(tradeData)) return;

      const response = await fetch(`${tradesApiUrl}/trades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(tradeData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        alert(`Failed to save trade: ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving trade:', error);
      alert(`Error saving trade: ${error.message}`);
    }
  };

  const viewTrade = (trade) => {
    console.log('viewTrade = ', trade);
    if (trade.status === 'confirmed' || trade.status === 'matured') {
      setTrade(trade);
      setShowConfirmedTrade(true);
    } else {
      setViewOnly(true);
      setShowModal(true);
      setTrade(trade);
    }
  };

  const handleTradeUpdate = (trade) => {
    setTrade(trade);
  };

  const [isTradeValid, setIsTradeValid] = useState(false);
  const handleValidationUpdate = (isValid) => {
    setIsTradeValid(isValid);
  };

  const getButtons = (trade) => {
    if (!canModifyTrade(trade)) {
      return (
        <div>
          <Button variant="primary" onClick={closeModal} className='mx-2'>
            Close
          </Button>
        </div>
      );
    }

    if (trade.status === 'confirmed' || trade.status === 'cancelled' || trade.status === 'matured') {
      if (trade.entity === user.party || getRole() === 'manager') {
        return (
          <div>
            <Button variant="primary" onClick={closeModal} className='mx-2'>
              Close
            </Button>
            <Button variant="danger" onClick={cancelTrade}>
              Cancel
            </Button>
          </div>
        )
      }

      return (
        <div>
          <Button variant="primary" onClick={closeModal} className='mx-2'>
            Close
          </Button>
        </div>
      )
    }

    if (trade.status === 'unconfirmed') {
      if (trade.cpty === user.party || getRole() === 'manager') {
        return (
          <div>
            <Button variant="primary" onClick={closeModal} className='mx-2'>
              Close
            </Button>
            <Button variant="danger" onClick={cancelTrade} className='mx-2'>
              Cancel
            </Button>
            <Button variant="success" onClick={confirmTrade}>
              Confirm
            </Button>
          </div>
        )
      }

      if (trade.entity === user.party || getRole() === 'manager') {
        return (
          <div>
            <Button variant="primary" onClick={closeModal} className='mx-2'>
              Close
            </Button>
            <Button variant="danger" onClick={cancelTrade}>
              Cancel
            </Button>
          </div>
        )
      }

      return (
        <div>
          <Button variant="primary" onClick={closeModal} className='mx-2'>
            Close
          </Button>
        </div>
      )
    }

    if (trade.status === 'sent') {
      if (trade.entity === user.party || getRole() === 'manager') {
        return (
          <div>
            <Button variant="primary" onClick={closeModal} className='mx-2'>
              Close
            </Button>
            <Button variant="danger" onClick={cancelTrade}>
              Cancel
            </Button>
          </div>
        )
      }
      if (trade.cpty === user.party || getRole() === 'manager') {
        return (
          <div>
            <Button variant="primary" onClick={closeModal} className='mx-2'>
              Close
            </Button>
            <Button variant="danger" onClick={cancelTrade} className='mx-2'>
              Cancel
            </Button>
            <Button variant="success" onClick={confirmTrade}>
              Confirm
            </Button>
          </div>
        )
      }
    }

    return (
      <div>
        <Button variant="primary" onClick={closeModal} className='mx-2'>
          Close
        </Button>
      </div>
    )
  };

  return (
    <Container fluid>
      <div className="mb-3">
        <strong>Party:</strong> {user?.party || 'Not set'} | <strong>Organization:</strong> {partyOrganization || 'Not set'}
      </div>
      <TradesGrid
        data={trades}
        title="Trades"
        onTradeSelect={viewTrade}
        party={user?.party}
        role={getRole()}
        onNewTrade={newTrade}
        canCreateTrade={canCreateTrade()}
      />
      <hr/>

      {/* Modal for TradeEntry */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{ (viewOnly) ? 'Pre-arranged Trade' : 'New pre-arranged trade'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TradeEntry isNew={true} user={user} trade={trade} onTradeUpdate={handleTradeUpdate} onValidationUpdate={handleValidationUpdate} viewOnly={viewOnly}/>
        </Modal.Body>
        <Modal.Footer>
          <div className="me-auto">
            { !isTradeValid && <p>Please complete all required fields</p> }
          </div>

          {
            (viewOnly) ? 

              getButtons(trade)
            
            :
            <div>
              <Button variant="danger" onClick={closeModal} className='mx-2'>
                Cancel
              </Button>
              <Button variant="success" onClick={openConfirmModal} disabled={!isTradeValid}>
                Send Trade
              </Button>
            </div>
          }
          
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={closeConfirmModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Trade</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ConfirmTrade tradeData={trade} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={closeConfirmModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={sendTrade}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmedTrade} onHide={closeConfirmedTrade} size="lg">
        <Modal.Body>
          <ConfirmTrade tradeData={trade} />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default TradeBlotter;
